import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Connects to data-controller="partners-navigation"
export default class extends Controller {
  static targets = [
    "householdInfoContainer", "householdInfoClosedContainer",
    "householdInfoOpenContainer", "householdInfoToggleSwitch",
    // Access View targets
    "assessmentOverlay", "providerOverlay",
    "filterPills", "showMoreButton", "providerCard", "donutChart"
  ]

  static values = {
    accessView: { type: Boolean, default: false },
    providerGeojson: { type: String, default: "" },
    zip: { type: String, default: "" },
    typeFilter: { type: String, default: "all" },
    partnerSlug: { type: String, default: "" }
  }

  connect() {
    if (this.accessViewValue) {
      this.selectedNpi = null
      this.initDonutCharts()

      // Donut chart targets live in the assessment overlay which is lazy-loaded.
      // Re-init once the assessment HTML has been injected into the DOM.
      document.addEventListener("access-view:assessment-ready", () => {
        this.initDonutCharts()
      }, { once: true })
    }
  }

  toggleHouseholdInfo(event) {
    if (this.targets.has("householdInfoContainer")) {
      this.householdInfoContainerTarget.classList.toggle("hidden")

      if (this.householdInfoContainerTarget.classList.contains("hidden")) {
        this.householdInfoToggleSwitchTarget.innerHTML = "Show settings to customize."
      } else {
        this.householdInfoToggleSwitchTarget.innerHTML = "Hide settings."
      }
    } else {
      this.householdInfoClosedContainerTarget.classList.toggle("hidden")
      this.householdInfoOpenContainerTarget.classList.toggle("hidden")
    }
  }

  // Access View: Select a provider from the list
  selectProvider(event) {
    if (!this.accessViewValue) return

    const npi = event.params.npi || event.currentTarget.dataset.npi
    if (!npi) return

    // Toggle selection
    if (this.selectedNpi === npi) {
      this.deselectProvider()
      return
    }

    this.selectedNpi = npi

    // Highlight the selected card
    if (this.hasProviderCardTarget) {
      this.providerCardTargets.forEach(card => {
        if (card.dataset.npi === npi) {
          card.classList.add("border-core-purple-500", "shadow-md")
          card.classList.remove("border-support-light-300")
        } else {
          card.classList.remove("border-core-purple-500", "shadow-md")
          card.classList.add("border-support-light-300")
        }
      })
    }

    // Show provider overlay, hide assessment overlay
    if (this.hasAssessmentOverlayTarget) {
      this.assessmentOverlayTarget.classList.add("hidden")
    }
    if (this.hasProviderOverlayTarget) {
      this.providerOverlayTarget.classList.remove("hidden")
      this.renderProviderOverlay(npi)
    }

    // Dispatch event for mapbox to highlight
    this.dispatch("providerSelected", { detail: { npi } })
  }

  deselectProvider() {
    this.selectedNpi = null

    // Remove all card highlights
    if (this.hasProviderCardTarget) {
      this.providerCardTargets.forEach(card => {
        card.classList.remove("border-core-purple-500", "shadow-md")
        card.classList.add("border-support-light-300")
      })
    }

    // Show assessment overlay, hide provider overlay
    if (this.hasAssessmentOverlayTarget) {
      this.assessmentOverlayTarget.classList.remove("hidden")
    }
    if (this.hasProviderOverlayTarget) {
      this.providerOverlayTarget.classList.add("hidden")
    }

    this.dispatch("providerDeselected")
  }

  // Access View: Render provider detail in the overlay
  renderProviderOverlay(npi) {
    if (!this.hasProviderOverlayTarget) return

    const card = this.providerCardTargets.find(c => c.dataset.npi === npi)
    if (!card) return

    const name = card.querySelector(".text-support-dark-800")?.textContent?.trim() || "Provider"
    const details = card.querySelectorAll(".text-support-dark-400")
    const badges = card.querySelectorAll("span[class*='bg-']")

    let badgeHtml = ""
    badges.forEach(b => { badgeHtml += b.outerHTML + " " })

    let detailText = ""
    details.forEach(d => { detailText += d.textContent.trim() + " " })

    this.providerOverlayTarget.innerHTML = `
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-support-dark-800">${name}</div>
          <button class="text-support-dark-300 hover:text-support-dark-600 text-lg" data-action="click->partners-navigation#deselectProvider">&times;</button>
        </div>
        <div class="text-xs text-support-dark-400">${detailText}</div>
        <div class="flex flex-wrap gap-1">${badgeHtml}</div>
        <div class="text-xs text-support-dark-300">NPI: ${npi}</div>
      </div>
    `
  }

  // Access View: Load more providers via fetch
  loadMoreProviders(event) {
    event.preventDefault()
    const url = event.currentTarget.href

    fetch(url, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => {
        const container = document.getElementById("access-view-provider-list")
        if (this.hasShowMoreButtonTarget) {
          this.showMoreButtonTarget.closest("div")?.remove()
        }
        container.insertAdjacentHTML("beforeend", html)
      })
  }

  // Access View: Filter by provider type (graceful degradation via link)
  filterByType(event) {
    // Let the link navigate normally - server-side filtering
  }

  // Access View: Initialize ApexCharts donut charts
  initDonutCharts() {
    if (!this.hasDonutChartTarget) return
    if (typeof ApexCharts === "undefined") return

    this.donutChartTargets.forEach(el => {
      const green = parseInt(el.dataset.green) || 0
      const yellow = parseInt(el.dataset.yellow) || 0
      const red = parseInt(el.dataset.red) || 0
      const total = green + yellow + red

      if (total === 0) {
        el.innerHTML = '<div class="flex items-center justify-center h-full text-xs text-support-dark-300">-</div>'
        return
      }

      const chart = new ApexCharts(el, {
        chart: {
          type: "donut",
          width: 60,
          height: 60,
          sparkline: { enabled: true }
        },
        series: [green, yellow, red],
        colors: ["#16a34a", "#eab308", "#dc2626"],
        labels: ["Green", "Yellow", "Red"],
        plotOptions: {
          pie: {
            donut: {
              size: "60%"
            }
          }
        },
        legend: { show: false },
        tooltip: { enabled: true },
        dataLabels: { enabled: false },
        states: {
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } }
        }
      })
      chart.render()
    })
  }
}
